import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ShieldCheck,
  AlertTriangle,
  Users,
  Camera,
  Lock,
  Mail,
  Phone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SEASON } from '../../shared/season.js';

type Section = {
  Icon: LucideIcon;
  title: string;
  body: React.ReactNode;
};

const sections: Section[] = [
  {
    Icon: ShieldCheck,
    title: 'Reģistrācijas informācijas precizitāte',
    body: (
      <>
        <p>
          Reģistrējoties Super Week nometnei, dalībnieks (vai viņa vecāks/aizbildnis) apliecina, ka
          visa anketā sniegtā informācija — vārds, vecums, kontaktinformācija, alerģijas un veselības
          piezīmes — ir patiesa, precīza un aktuāla.
        </p>
        <p>
          Apzināti nepatiesas informācijas sniegšana ir iemesls reģistrācijas atcelšanai bez
          iepriekšēja brīdinājuma.
        </p>
      </>
    ),
  },
  {
    Icon: AlertTriangle,
    title: 'Dalība uz savu atbildību',
    body: (
      <>
        <p>
          Mēs ar prieku organizējam nometni un cenšamies, lai katra aktivitāte būtu pārdomāta un
          droša. Tomēr piedalīšanās sporta, radošajās un izklaides aktivitātēs vienmēr ietver
          zināmu risku, tāpēc dalība notiek brīvprātīgi un uz dalībnieka paša
          (vai viņa vecāka/aizbildņa) atbildību.
        </p>
        <p>
          Tas nozīmē, ka Super Week organizatori, brīvprātīgie un partneri neuzņemas
          materiālu atbildību par traumām, savainojumiem vai veselības problēmām, kas varētu
          rasties aktivitāšu laikā, kā arī par personīgām mantām, kas tiek atstātas bez
          uzraudzības, pazūd vai tiek sabojātas.
        </p>
        <p>
          Mēs ļoti lūdzam dalībniekus ievērot komandas norādījumus, izvēlēties aktivitātes
          atbilstoši savām spējām un sazināties ar mums, ja kaut kas šķiet nedrošs vai
          neskaidrs — mēs labprāt palīdzēsim.
        </p>
      </>
    ),
  },
  {
    Icon: Users,
    title: 'Bērnu uzraudzība un atbalsts',
    body: (
      <>
        <p>
          Super Week ir dienas nometne, ne aizbildņu pakalpojums. Mūsu komanda nodrošina
          aktivitātes, programmu un drošu vidi, taču nepārtrauktu individuālu pieskatīšanu un
          aizbildņa lomu uzņemas vecāks vai likumīgais aizbildnis. Vecāki ir tie, kas pazīst savu
          bērnu vislabāk un atbild par viņa uzvedību, drošību un labsajūtu nometnes laikā.
        </p>
        <p>
          Praktiski tas nozīmē:
        </p>
        <ul className="list-disc list-outside ml-6 space-y-1.5 text-gray-700">
          <li>
            bērniem līdz 7 gadu vecumam nepieciešama vecāku pavadība — bērnu nometnē pavada
            vecāks vai cits pieaugušais, kurš ir atbildīgs par bērnu visas dienas garumā;
          </li>
          <li>
            vecāks rūpējas par bērnam piemērotu apģērbu, apaviem, saules aizsardzību, ēdināšanas
            īpatnībām un nepieciešamajiem medikamentiem;
          </li>
          <li>
            par alerģijām, hroniskām saslimšanām vai citām īpašām vajadzībām vecāks informē
            organizatorus rakstiski jau reģistrācijas laikā.
          </li>
        </ul>
        <p>
          Mēs vienmēr esam pieejami, ja vecākam vai bērnam ir jautājumi — vienkārši
          pienāc klāt jebkuram no organizatoriem.
        </p>
      </>
    ),
  },
  {
    Icon: Camera,
    title: 'Foto un video uzņemšana',
    body: (
      <>
        <p>
          Nometnes laikā organizatori uzņems fotogrāfijas un video, kas dokumentē notikumu.
          Reģistrējoties dalībnieks (vai viņa vecāks/aizbildnis) piekrīt, ka šīs fotogrāfijas un
          video ieraksti var tikt izmantoti:
        </p>
        <ul className="list-disc list-outside ml-6 space-y-1.5 text-gray-700">
          <li>Super Week mājaslapā un sociālajos tīklos (Facebook, Instagram, YouTube u.c.);</li>
          <li>nākamo nometņu reklāmas un informatīvajos materiālos;</li>
          <li>presē un partneru publikācijās par pasākumu.</li>
        </ul>
        <p>
          Ja kāda iemesla dēļ nevēlies, lai tava (vai tava bērna) fotogrāfija tiktu publicēta,
          lūdzu paziņo organizatoriem rakstiski (uz e-pastu) pirms nometnes sākuma vai informē
          komandu uz vietas.
        </p>
      </>
    ),
  },
  {
    Icon: Lock,
    title: 'Personas datu apstrāde',
    body: (
      <>
        <p>
          Anketā sniegtie personas dati tiek izmantoti tikai nometnes organizēšanas vajadzībām —
          reģistrācijai, saziņai, drošības informācijai un statistikai. Datus glabā Super Week
          organizatoru komanda un tie netiek nodoti trešajām pusēm bez tavas piekrišanas, izņemot
          gadījumus, kad to pieprasa likums.
        </p>
        <p>
          Tev ir tiesības pieprasīt piekļuvi saviem datiem, to labošanu vai dzēšanu, sazinoties ar
          mums pa zemāk norādītajiem kontaktiem.
        </p>
      </>
    ),
  },
];

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 py-12 md:py-16 max-w-3xl">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Atpakaļ uz sākumlapu
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">
            Noteikumi un nosacījumi
          </p>
          <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-3">
            Super Week {SEASON.year} — noteikumi
          </h1>
          <p className="text-gray-600">
            Pirms reģistrācijas, lūdzu, izlasi šos noteikumus. Atzīmējot anketā piekrišanas
            izvēles rūtiņu, tu apstiprini, ka tos esi izlasījis un tiem piekrīti.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map(({ Icon, title, body }, i) => (
            <motion.section
              key={title}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 * i }}
              className="bg-white border border-gray-200 rounded-2xl p-6 md:p-7 shadow-sm"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shrink-0 shadow">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">{title}</h2>
              </div>
              <div className="space-y-3 text-gray-700 leading-relaxed pl-0 md:pl-[60px]">
                {body}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Contact */}
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-10 bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-100 rounded-2xl p-6 md:p-7"
        >
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">Jautājumi par noteikumiem?</h2>
          <p className="text-gray-700 mb-4">
            Ja kaut kas šajos noteikumos nav skaidrs vai vēlies iebilst pret savu fotogrāfiju
            izmantošanu, sazinies ar mums:
          </p>
          <div className="flex flex-col sm:flex-row gap-3 text-sm">
            <a
              href="mailto:ezaurins@pm.me"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 hover:border-primary-300 hover:shadow transition-all"
            >
              <Mail className="w-4 h-4 text-primary-600" />
              <span className="text-gray-700 font-medium">ezaurins@pm.me</span>
            </a>
            <a
              href="tel:+37124904140"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 hover:border-primary-300 hover:shadow transition-all"
            >
              <Phone className="w-4 h-4 text-primary-600" />
              <span className="text-gray-700 font-medium">+371 24 904 140</span>
            </a>
          </div>
        </motion.section>

        {/* Keep the "last updated" date truthful — bump it only when the
            terms content is actually reviewed (do that before opening
            registration each season). */}
        <p className="text-center text-xs text-gray-400 mt-10">
          Pēdējoreiz atjaunots: 2026. gada maijs · Super Week {SEASON.year}
        </p>
      </div>
    </div>
  );
};

export default Terms;
